import { useDispatch } from "react-redux";
import { useCallback, useEffect, useRef, useState } from "react";

import { checkFonepayQrPaymentStatus } from "../api/bill";
import { notificationAdd } from "../actions/notification";

export type FonepayPaymentStatus = "PENDING" | "VERIFIED" | "COMPLETED" | "FAILED";
export type FonepayWsStatus = "IDLE" | "CONNECTING" | "CONNECTED" | "DISCONNECTED" | "ERROR";

interface UseFonepayWSSProps {
  webSocketUrl: string;
  productNumber: string;
  rcId?: number;
}

interface WsTransactionStatus {
  success?: boolean;
  message?: string;
  QRVerified?: boolean;

  remarks1?: string;
  remarks2?: string;
  transactionDate?: string;
  productNumber?: string;
  amount?: string;

  commissionType?: string;
  commissionAmount?: number;
  totalCalculatedAmount?: number;

  paymentSuccess?: boolean;
}

interface FonepayWebSocketMessage {
  merchantId?: number;
  deviceId?: string;
  transactionStatus?: string | WsTransactionStatus;
}

export function useFonepayWSS({ webSocketUrl, productNumber, rcId }: UseFonepayWSSProps): {
  wsStatus: FonepayWsStatus;
  paymentStatus: FonepayPaymentStatus;
  isChecking: boolean;
  reconnect: () => void;
  checkStatus: () => Promise<void>;
} {
  const dispatch = useDispatch();
  const socketRef = useRef<WebSocket | null>(null);

  // local states
  const [isChecking, setIsChecking] = useState(false);
  const [wsStatus, setWsStatus] = useState<FonepayWsStatus>("IDLE");
  const [paymentStatus, setPaymentStatus] = useState<FonepayPaymentStatus>("PENDING");

  /**
   * Check payment status using Fonepay's REST API.
   *
   * Fonepay's documentation says that after receiving a WebSocket
   * notification, the merchant should call the Check QR Request Status API.
   */
  const checkStatus = useCallback(async () => {
    if (!productNumber || !rcId) {
      dispatch(
        notificationAdd({
          id: new Date().getTime(),
          variant: "warning",
          message: "Cannot check fonepay status. Missiong product number or rcId.",
          autoTimeout: true
        })
      );

      return;
    }

    if (isChecking) return;

    setIsChecking(true);

    try {
      const response = await checkFonepayQrPaymentStatus({
        productNumber,
        rcId
      });

      if (!response.success) {
        dispatch(
          notificationAdd({
            id: new Date().getTime(),
            variant: "error",
            message: `Fonepay status check failed ${response.message}`,
            autoTimeout: true
          })
        );

        return;
      }

      switch (response?.data?.paymentStatus?.toLowerCase()) {
        case "success":
          setPaymentStatus("COMPLETED");
          // socketRef.current?.close();

          dispatch(
            notificationAdd({
              id: new Date().getTime(),
              variant: "success",
              message: "Payment successful.",
              autoTimeout: true
            })
          );
          break;

        case "failed":
          setPaymentStatus("FAILED");
          // socketRef.current?.close();

          dispatch(
            notificationAdd({
              id: new Date().getTime(),
              variant: "error",
              message: "Payment failed.",
              autoTimeout: true
            })
          );
          break;

        default:
          setPaymentStatus("PENDING");
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      dispatch(
        notificationAdd({
          id: Date.now(),
          variant: "error",
          message:
            error instanceof Error ? error.message : "Unable to check Fonepay payment status.",
          autoTimeout: true
        })
      );
    } finally {
      setIsChecking(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productNumber, rcId]);

  /**
   * Connect to the websocket edpoint provided by Fonepay.
   * Fonepay returns websocket endpoint in response to successful QR string generation.
   */
  const connect = useCallback(() => {
    socketRef.current?.close();
    setWsStatus("CONNECTING");

    let socket: WebSocket;

    try {
      socket = new WebSocket(webSocketUrl);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setWsStatus("ERROR");
      return;
    }

    socketRef.current = socket;

    socket.onopen = () => {
      setWsStatus("CONNECTED");
    };

    socket.onerror = () => {
      setWsStatus("ERROR");
    };

    socket.onclose = () => {
      setWsStatus("DISCONNECTED");

      if (socketRef.current === socket) {
        socketRef.current = null;
      }
    };

    socket.onmessage = async (event: MessageEvent) => {
      try {
        const payload: FonepayWebSocketMessage =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;

        if (!payload.transactionStatus) {
          return;
        }

        const transaction: WsTransactionStatus =
          typeof payload.transactionStatus === "string"
            ? JSON.parse(payload.transactionStatus)
            : payload.transactionStatus;

        // QR scanned
        if (transaction.message === "VERIFIED" || transaction.QRVerified === true) {
          setPaymentStatus("VERIFIED");
          return;
        }

        // Payment completed
        if (transaction.paymentSuccess === true || transaction.message === "Request Complete") {
          // NOTE: At this point we dont need to check the status with REST endpoint.
          // We can just update the status
          await checkStatus();
          return;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        dispatch(
          notificationAdd({
            id: new Date().getTime(),
            variant: "error",
            message: error?.message || "Unable to parse websocket message",
            autoTimeout: true
          })
        );
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webSocketUrl, checkStatus]);

  // diconnect
  const disconnect = useCallback(() => {
    if (!socketRef?.current) {
      return;
    }

    socketRef?.current?.close();
    socketRef.current = null;

    setWsStatus("DISCONNECTED");
  }, []);

  // reconnect
  const reconnect = useCallback(() => {
    disconnect();
    setTimeout(() => {
      connect();
    }, 100);
  }, [connect, disconnect]);

  // effect hooks
  useEffect(() => {
    if (webSocketUrl) {
      connect();
    }

    return () => disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webSocketUrl]);

  return {
    wsStatus,
    paymentStatus,
    isChecking,
    reconnect,
    checkStatus
  };
}
